import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { 
  Menu, X, Search, ChevronDown, Phone, MessageCircle
} from "lucide-react";
import { companyInfo } from "../mock";
import { openChat } from "../utils/chatUtils";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  
  // Hover intent timers
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  // Handle hover intent for mega menu
  const handleMenuEnter = (menuName) => {
    // Clear any pending close timer
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    
    // Set open delay (150ms)
    openTimerRef.current = setTimeout(() => {
      setActiveMenu(menuName);
    }, 150);
  };

  const handleMenuLeave = () => {
    // Clear any pending open timer
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    
    // Set close delay (275ms)
    closeTimerRef.current = setTimeout(() => {
      setActiveMenu(null);
    }, 275);
  };

  const serviceCategories = [
    {
      title: "Core Publishing Services",
      items: [
        { 
          name: "Book Writing", 
          gifUrl: "https://customer-assets.emergentagent.com/job_bookpub-service/artifacts/daw30gby_Book%20Writing.gif",
          path: "/services/book-writing", 
          description: "Professional ghostwriting services" 
        },
        { 
          name: "Book Publishing", 
          gifUrl: "https://customer-assets.emergentagent.com/job_bookpub-service/artifacts/3r60pv54_Book%20Publishing.gif",
          path: "/services/book-publishing", 
          description: "Complete publishing solutions" 
        },
        { 
          name: "Book Marketing", 
          gifUrl: "https://customer-assets.emergentagent.com/job_bookpub-service/artifacts/v6da3wsg_Book%20Marketing.gif",
          path: "/services/book-marketing", 
          description: "Strategic marketing campaigns" 
        },
        { 
          name: "Audiobook Production", 
          gifUrl: "https://customer-assets.emergentagent.com/job_bookpub-service/artifacts/3rg76y84_Audiobook.gif",
          path: "/services/audiobook-production", 
          description: "Professional audiobook creation" 
        }
      ]
    },
    {
      title: "Production Services",
      items: [
        { 
          name: "Book Editing", 
          gifUrl: "https://customer-assets.emergentagent.com/job_bookpub-service/artifacts/6fqpo795_Book%20Editing.gif",
          path: "/services/book-editing", 
          description: "Comprehensive editing services" 
        },
        { 
          name: "Book Printing", 
          gifUrl: "https://customer-assets.emergentagent.com/job_bookpub-service/artifacts/mkr6zm0l_Book%20Printing.gif",
          path: "/services/book-printing", 
          description: "Print-on-demand & bulk printing" 
        }
      ]
    },
    {
      title: "Other Services",
      items: [
        { 
          name: "Book Video Trailer", 
          gifUrl: "https://customer-assets.emergentagent.com/job_bookpub-service/artifacts/3rg76y84_Audiobook.gif",
          path: "/services/book-video-trailer", 
          description: "Cinematic book trailers" 
        },
        { 
          name: "Web Design & SEO", 
          gifUrl: "https://customer-assets.emergentagent.com/job_bookpub-service/artifacts/3r60pv54_Book%20Publishing.gif",
          path: "/services/web-design-seo", 
          description: "Author websites & optimization" 
        },
        { 
          name: "Illustration Services", 
          gifUrl: "https://customer-assets.emergentagent.com/job_bookpub-service/artifacts/j1a1yaoa_Book%20Design.gif",
          path: "/services/illustration-services", 
          description: "Custom illustrations" 
        },
        { 
          name: "Articles Services", 
          gifUrl: "https://customer-assets.emergentagent.com/job_bookpub-service/artifacts/am46oi0t_Articles.gif",
          path: "/services/articles-services", 
          description: "Professional article writing" 
        },
        { 
          name: "Book Design Services", 
          gifUrl: "https://customer-assets.emergentagent.com/job_bookpub-service/artifacts/mkr6zm0l_Book%20Printing.gif",
          path: "/services/book-design-services", 
          description: "Cover & interior design" 
        },
        { 
          name: "Blog Writing Services", 
          gifUrl: "https://customer-assets.emergentagent.com/job_bookpub-service/artifacts/16v6fn6h_Blog%20Writing.gif",
          path: "/services/blog-writing-services", 
          description: "Engaging blog content" 
        },
        { 
          name: "Web Content Writing", 
          gifUrl: "https://customer-assets.emergentagent.com/job_bookpub-service/artifacts/prgf71ka_Web%20Content%20Writing.gif",
          path: "/services/web-content-writing", 
          description: "SEO-optimized website copy" 
        }
      ]
    }
  ];

  const mainNavLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Services", path: "/#services", hasMegaMenu: true },
    { name: "Blog", path: "/blog" },
    { name: "Contact", path: "/contact" }
  ];

  const scrollToSection = (path) => {
    if (path.startsWith("#")) {
      const element = document.querySelector(path);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
    setMobileMenuOpen(false);
    setActiveMenu(null);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    console.log("Search query:", searchQuery);
    // Implement search functionality here
    alert(`Searching for: ${searchQuery}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-[#000000] border-b border-[#3f4816] shadow-lg h-14 md:h-[66px] lg:h-[76px]" role="banner">
      {/* Main Navigation */}
      <nav className="container mx-auto px-6 h-full" role="navigation" aria-label="Main navigation">
        <div className="flex items-center justify-between h-full">
          {/* Logo - Updated to logo-02.png */}
          <Link to="/" className="flex items-center" onClick={() => window.scrollTo(0, 0)}>
            <img 
              src="/assets/img/brand/logo-light.png"
              alt="Book Creation Studio"
              className="h-10 md:h-12 lg:h-14 w-auto hover:opacity-90 transition-opacity"
            />
          </Link>

          {/* Desktop Navigation - Centered & Evenly Spaced */}
          <nav className="hidden lg:flex items-center gap-8">
            {mainNavLinks.map((link) => {
              // Determine if this link is active
              const isActive = link.path === "/" 
                ? location.pathname === "/" 
                : link.path.startsWith("#") 
                  ? location.pathname === "/" && location.hash === link.path
                  : location.pathname.startsWith(link.path);
              
              return (
                <div
                  key={link.name}
                  className="relative flex items-center h-full"
                  onMouseEnter={() => link.hasMegaMenu && handleMenuEnter(link.name)}
                  onMouseLeave={() => link.hasMegaMenu && handleMenuLeave()}
                >
                  <a
                    href={link.path}
                    onClick={(e) => {
                      if (link.path.startsWith("#")) {
                        e.preventDefault();
                        scrollToSection(link.path);
                      }
                    }}
                    className={`transition-colors duration-300 flex items-center gap-1 text-sm font-medium uppercase tracking-wide relative group ${
                      isActive 
                        ? "text-[#D4AF37]" 
                        : "text-white hover:text-[#D4AF37]"
                    }`}
                  >
                    {link.name}
                    {link.hasMegaMenu && <ChevronDown size={14} className={`transition-transform duration-300 ${activeMenu === link.name ? "rotate-180" : ""}`} />}
                    {/* Subtle underline on hover */}
                    <span className={`absolute bottom-[-2px] left-0 h-[1px] bg-[#D4AF37] transition-all duration-300 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}></span>
                  </a>

                {/* Mega Menu Dropdown - Opaque Premium Design */}
                {link.hasMegaMenu && activeMenu === link.name && (
                  <div 
                    className="fixed left-0 right-0 top-[76px] mx-auto w-full max-w-7xl px-6 animate-fadeIn z-50"
                    onMouseEnter={() => {
                      if (closeTimerRef.current) {
                        clearTimeout(closeTimerRef.current);
                        closeTimerRef.current = null;
                      }
                    }}
                    onMouseLeave={handleMenuLeave}
                  >
                    <div className="bg-[rgba(10,10,10,0.93)] backdrop-blur-[7px] border-x border-b border-[#3f4816]/50 shadow-elevation overflow-hidden relative">
                      {/* Inner border for premium feel */}
                      <div className="absolute inset-0 border border-[#D4AF37]/10 pointer-events-none"></div>
                      <div className="px-8 py-6">
                      {/* Search Bar - Ultra Compact */}
                      <div className="mb-4">
                        <form onSubmit={handleSearch} className="flex gap-2">
                          <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#888680]" size={16} />
                            <Input
                              type="text"
                              placeholder="Search services..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="bg-[#000000]/50 border-[#3f4816] text-[#dfddd6] placeholder:text-[#888680] pl-9 h-9 text-sm focus:border-[#D4AF37] transition-colors"
                            />
                          </div>
                          <Button type="submit" className="btn-primary h-9 px-4 text-sm">
                            Search
                          </Button>
                        </form>
                      </div>

                      {/* Service Categories - Clean Grid with Better Spacing */}
                      <div className="grid md:grid-cols-3 gap-6">
                        {serviceCategories.map((category, idx) => (
                          <div key={idx}>
                            <h3 className="text-[#D4AF37] font-semibold text-sm uppercase mb-3 tracking-wide border-b border-[#3f4816]/30 pb-2">
                              {category.title}
                            </h3>
                            <ul className="space-y-1">
                              {category.items.map((item) => {
                                return (
                                  <li key={item.name}>
                                    <Link
                                      to={item.path}
                                      onClick={() => {
                                        setActiveMenu(null);
                                        setMobileMenuOpen(false);
                                        window.scrollTo(0, 0);
                                      }}
                                      className="mega-menu-item flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-[#D4AF37]/8 transition-colors duration-200 group relative"
                                    >
                                      <div className="w-9 h-9 bg-white/95 rounded flex items-center justify-center flex-shrink-0 group-hover:bg-white transition-colors duration-200 overflow-hidden">
                                        {item.gifUrl ? (
                                          <img 
                                            src={item.gifUrl} 
                                            alt={item.name}
                                            className="w-7 h-7 object-contain"
                                            style={{ imageRendering: "crisp-edges" }}
                                          />
                                        ) : (
                                          <div className="w-7 h-7 bg-[#F8D26B]" />
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0 flex flex-col justify-center gap-0.5">
                                        <div className="text-[#dfddd6] font-semibold text-[15px] group-hover:text-[#D4AF37] transition-colors truncate">
                                          {item.name}
                                        </div>
                                        <div className="text-[#888680] text-[13px] leading-snug group-hover:text-[#b8b8b8] transition-colors line-clamp-1">
                                          {item.description}
                                        </div>
                                      </div>
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ))}
                      </div>

                      {/* CTA in Mega Menu - Ultra Compact */}
                      <div className="mt-3 pt-2.5 border-t border-[#3f4816]/30 text-center">
                        <p className="text-[#888680] mb-1.5 text-xs">Ready to start your publishing journey?</p>
                        <Button 
                          className="btn-primary text-xs px-4 py-1.5 bg-[#D4AF37] hover:bg-[#E5C158] text-[#000000]"
                          onClick={(e) => {
                            e.preventDefault();
                            scrollToSection("#contact");
                          }}
                        >
                          Get Started Today
                        </Button>
                      </div>
                    </div>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </nav>

          {/* Contact Number, Divider & Social Icons (Desktop) */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Contact Number with Icon */}
            <a 
              href={`tel:${companyInfo.phone}`} 
              className="flex items-center gap-2 group transition-colors font-semibold text-sm"
            >
              <Phone size={16} strokeWidth={2} className="text-[#D4AF37]" />
              <span className="tracking-tight text-white group-hover:text-[#D4AF37] transition-colors">{companyInfo.phone}</span>
            </a>
            
            {/* Elegant Gold Divider */}
            <div className="w-[1px] h-6 bg-[#D4AF37] opacity-40"></div>
            
            {/* Chat Button */}
            <Button
              onClick={openChat}
              className="js-chat bg-[#F4D03F] hover:bg-[#D4AF37] text-black font-semibold px-4 py-2 h-9 text-sm flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <MessageCircle size={16} strokeWidth={2} />
              Chat Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-[#F4D03F] p-2"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation - Full Screen Overlay */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-[88px] bg-[#000000] z-50 overflow-y-auto">
            <nav className="container mx-auto px-4 py-6">
              {/* Mobile Search */}
              <div className="mb-6">
                <form onSubmit={handleSearch} className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Search services..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="bg-[#1a1a1a] border-[#3f4816] text-[#dfddd6] placeholder:text-[#888680] h-12"
                  />
                  <Button type="submit" className="btn-primary h-12 px-6">
                    <Search size={20} />
                  </Button>
                </form>
              </div>

              {/* Mobile Menu Links */}
              {mainNavLinks.map((link) => (
                <div key={link.name} className="border-b border-[#3f4816]/30">
                  {link.hasMegaMenu ? (
                    <div>
                      <button
                        onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                        className="w-full flex items-center justify-between py-4 text-[#F4D03F] hover:text-[#F8D26B] transition-colors duration-300 text-lg font-semibold"
                      >
                        <span>{link.name}</span>
                        <ChevronDown size={24} className={`transition-transform duration-300 ${mobileServicesOpen ? "rotate-180" : ""}`} />
                      </button>
                      {mobileServicesOpen && (
                        <div className="pb-4 space-y-4">
                          {serviceCategories.map((category, idx) => (
                            <div key={idx}>
                              <div className="text-[#D4AF37] text-sm font-bold mb-3 uppercase tracking-wider border-b border-[#3f4816]/50 pb-2">
                                {category.title}
                              </div>
                              <div className="space-y-2">
                                {category.items.map((item) => (
                                  <Link
                                    key={item.name}
                                    to={item.path}
                                    onClick={() => {
                                      setMobileMenuOpen(false);
                                      setMobileServicesOpen(false);
                                      window.scrollTo(0, 0);
                                    }}
                                    className="flex items-start gap-3 py-3 px-3 text-[#dfddd6] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-lg transition-all"
                                  >
                                    <div className="w-10 h-10 bg-white/95 rounded-lg flex items-center justify-center flex-shrink-0">
                                      {item.gifUrl ? (
                                        <img 
                                          src={item.gifUrl} 
                                          alt={item.name}
                                          className="w-7 h-7 object-contain"
                                        />
                                      ) : (
                                        <div className="w-7 h-7 bg-[#F8D26B]" />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-semibold text-base">{item.name}</div>
                                      <div className="text-[#888680] text-sm leading-snug">{item.description}</div>
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <a
                      href={link.path}
                      onClick={(e) => {
                        if (link.path.startsWith("#")) {
                          e.preventDefault();
                          scrollToSection(link.path);
                        }
                        setMobileMenuOpen(false);
                      }}
                      className="block py-4 text-[#F4D03F] hover:text-[#F8D26B] transition-colors duration-300 text-lg font-semibold"
                    >
                      {link.name}
                    </a>
                  )}
                </div>
              ))}

              {/* Contact Info in Mobile Menu */}
              <div className="mt-6 pt-6 border-t border-[#3f4816]">
                <a 
                  href={`tel:${companyInfo.phone}`}
                  className="flex items-center gap-3 text-[#F4D03F] hover:text-[#F8D26B] mb-4 text-lg"
                >
                  <Phone size={20} />
                  <span>{companyInfo.phone}</span>
                </a>
                {companyInfo.socialLinks && (
                  <div className="flex gap-4">
                    <a href={companyInfo.socialLinks.facebook} className="w-10 h-10 rounded-full border border-[#F4D03F]/40 flex items-center justify-center text-[#F4D03F] hover:bg-[#F4D03F]/10 transition-all">
                      <Facebook size={20} />
                    </a>
                    <a href={companyInfo.socialLinks.twitter} className="w-10 h-10 rounded-full border border-[#F4D03F]/40 flex items-center justify-center text-[#F4D03F] hover:bg-[#F4D03F]/10 transition-all">
                      <Twitter size={20} />
                    </a>
                    <a href={companyInfo.socialLinks.instagram} className="w-10 h-10 rounded-full border border-[#F4D03F]/40 flex items-center justify-center text-[#F4D03F] hover:bg-[#F4D03F]/10 transition-all">
                      <Instagram size={20} />
                    </a>
                    <a href={companyInfo.socialLinks.linkedin} className="w-10 h-10 rounded-full border border-[#F4D03F]/40 flex items-center justify-center text-[#F4D03F] hover:bg-[#F4D03F]/10 transition-all">
                      <Linkedin size={20} />
                    </a>
                  </div>
                )}
              </div>
            </nav>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;